/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Auth client type filter (mcp:auth and mcp:blocked_auth scopes only).
 */
export const AgentPolicySelectorClientType = {
  RemoteSession: "remote_session",
  ServiceAccount: "service_account",
  StaticCredential: "static_credential",
  PassThrough: "pass_through",
  Ema: "ema",
  Wildcard: "*",
} as const;
/**
 * Auth client type filter (mcp:auth and mcp:blocked_auth scopes only).
 */
export type AgentPolicySelectorClientType = OpenEnum<
  typeof AgentPolicySelectorClientType
>;

/**
 * Tool disposition filter (MCP scopes only).
 */
export const AgentPolicySelectorDisposition = {
  ReadOnly: "read_only",
  Destructive: "destructive",
  Idempotent: "idempotent",
  OpenWorld: "open_world",
} as const;
/**
 * Tool disposition filter (MCP scopes only).
 */
export type AgentPolicySelectorDisposition = OpenEnum<
  typeof AgentPolicySelectorDisposition
>;

/**
 * The kind of resource this selector targets.
 */
export const AgentPolicySelectorResourceKind = {
  Project: "project",
  Mcp: "mcp",
  Org: "org",
  Environment: "environment",
  Skill: "skill",
  Assistant: "assistant",
  RiskPolicy: "risk_policy",
  Chat: "chat",
  Agent: "agent",
  McpAuthClient: "mcp_auth_client",
  Wildcard: "*",
} as const;
/**
 * The kind of resource this selector targets.
 */
export type AgentPolicySelectorResourceKind = OpenEnum<
  typeof AgentPolicySelectorResourceKind
>;

/**
 * A constraint that narrows which resources an agent grant applies to.
 */
export type AgentPolicySelector = {
  /**
   * Auth client type filter (mcp:auth and mcp:blocked_auth scopes only).
   */
  clientType?: AgentPolicySelectorClientType | undefined;
  /**
   * Personal connection owner filter (mcp:auth and mcp:blocked_auth scopes on remote_session clients only): a user ID, '*', or '$represented_user'.
   */
  connectionOwner?: string | undefined;
  /**
   * Tool disposition filter (MCP scopes only).
   */
  disposition?: AgentPolicySelectorDisposition | undefined;
  /**
   * Project filter (MCP, environment, assistant, mcp:auth and mcp:blocked_auth scopes).
   */
  projectId?: string | undefined;
  /**
   * The resource identifier, or '*' for all resources of this kind.
   */
  resourceId: string;
  /**
   * The kind of resource this selector targets.
   */
  resourceKind: AgentPolicySelectorResourceKind;
  /**
   * Server identity filter (risk policy scopes only).
   */
  serverIdentity?: string | undefined;
  /**
   * Server URL filter (risk policy scopes only).
   */
  serverUrl?: string | undefined;
  /**
   * Specific tool name filter (MCP scopes only).
   */
  tool?: string | undefined;
  /**
   * Upstream OAuth client filter (mcp:auth and mcp:blocked_auth scopes on remote_session clients only). Together with connection_owner it names one person's connection across every auth client sharing that upstream client.
   */
  upstreamClient?: string | undefined;
};

/** @internal */
export const AgentPolicySelectorClientType$inboundSchema: z.ZodMiniType<
  AgentPolicySelectorClientType,
  unknown
> = openEnums.inboundSchema(AgentPolicySelectorClientType);
/** @internal */
export const AgentPolicySelectorClientType$outboundSchema: z.ZodMiniType<
  string,
  AgentPolicySelectorClientType
> = openEnums.outboundSchema(AgentPolicySelectorClientType);

/** @internal */
export const AgentPolicySelectorDisposition$inboundSchema: z.ZodMiniType<
  AgentPolicySelectorDisposition,
  unknown
> = openEnums.inboundSchema(AgentPolicySelectorDisposition);
/** @internal */
export const AgentPolicySelectorDisposition$outboundSchema: z.ZodMiniType<
  string,
  AgentPolicySelectorDisposition
> = openEnums.outboundSchema(AgentPolicySelectorDisposition);

/** @internal */
export const AgentPolicySelectorResourceKind$inboundSchema: z.ZodMiniType<
  AgentPolicySelectorResourceKind,
  unknown
> = openEnums.inboundSchema(AgentPolicySelectorResourceKind);
/** @internal */
export const AgentPolicySelectorResourceKind$outboundSchema: z.ZodMiniType<
  string,
  AgentPolicySelectorResourceKind
> = openEnums.outboundSchema(AgentPolicySelectorResourceKind);

/** @internal */
export const AgentPolicySelector$inboundSchema: z.ZodMiniType<
  AgentPolicySelector,
  unknown
> = z.pipe(
  z.object({
    client_type: z.optional(AgentPolicySelectorClientType$inboundSchema),
    connection_owner: z.optional(z.string()),
    disposition: z.optional(AgentPolicySelectorDisposition$inboundSchema),
    project_id: z.optional(z.string()),
    resource_id: z.string(),
    resource_kind: AgentPolicySelectorResourceKind$inboundSchema,
    server_identity: z.optional(z.string()),
    server_url: z.optional(z.string()),
    tool: z.optional(z.string()),
    upstream_client: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "client_type": "clientType",
      "connection_owner": "connectionOwner",
      "project_id": "projectId",
      "resource_id": "resourceId",
      "resource_kind": "resourceKind",
      "server_identity": "serverIdentity",
      "server_url": "serverUrl",
      "upstream_client": "upstreamClient",
    });
  }),
);
/** @internal */
export type AgentPolicySelector$Outbound = {
  client_type?: string | undefined;
  connection_owner?: string | undefined;
  disposition?: string | undefined;
  project_id?: string | undefined;
  resource_id: string;
  resource_kind: string;
  server_identity?: string | undefined;
  server_url?: string | undefined;
  tool?: string | undefined;
  upstream_client?: string | undefined;
};

/** @internal */
export const AgentPolicySelector$outboundSchema: z.ZodMiniType<
  AgentPolicySelector$Outbound,
  AgentPolicySelector
> = z.pipe(
  z.object({
    clientType: z.optional(AgentPolicySelectorClientType$outboundSchema),
    connectionOwner: z.optional(z.string()),
    disposition: z.optional(AgentPolicySelectorDisposition$outboundSchema),
    projectId: z.optional(z.string()),
    resourceId: z.string(),
    resourceKind: AgentPolicySelectorResourceKind$outboundSchema,
    serverIdentity: z.optional(z.string()),
    serverUrl: z.optional(z.string()),
    tool: z.optional(z.string()),
    upstreamClient: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      clientType: "client_type",
      connectionOwner: "connection_owner",
      projectId: "project_id",
      resourceId: "resource_id",
      resourceKind: "resource_kind",
      serverIdentity: "server_identity",
      serverUrl: "server_url",
      upstreamClient: "upstream_client",
    });
  }),
);

export function agentPolicySelectorToJSON(
  agentPolicySelector: AgentPolicySelector,
): string {
  return JSON.stringify(
    AgentPolicySelector$outboundSchema.parse(agentPolicySelector),
  );
}
export function agentPolicySelectorFromJSON(
  jsonString: string,
): SafeParseResult<AgentPolicySelector, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AgentPolicySelector$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AgentPolicySelector' from JSON`,
  );
}
